import React, { useState, useRef, useEffect } from "react";
import { Button, Container, Form, Alert } from "react-bootstrap";
import { FileDrop } from "react-file-drop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import ServiceCalls from "../utils/serviceCalls.js";
import { Loading } from "./Loading";
import "../styles/PopUps.css";
import "../styles/Admin.css";
import "../styles/Vendor.css";

const FILE_NAME_PATTERN = /^SP_(Supplier|Invoice|InvoicePaymentXREF|Payment)\.(.+)\.csv$/i;
const BATCH_RUN_ID_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;
const INVALID_BATCH_RUN_ID_MESSAGE = "Batch Run ID can only contain letters, numbers, hyphens, and underscores (up to 50 characters).";
const MAX_BATCH_SIZE_BYTES = 250 * 1024 * 1024;
const TYPE_CASING = { supplier: "Supplier", invoice: "Invoice", invoicepaymentxref: "InvoicePaymentXREF", payment: "Payment" };
const FORM_KEY = { Supplier: "supplier", Invoice: "invoice", InvoicePaymentXREF: "invoicePaymentXREF", Payment: "payment" };

const detectFile = (fileName) => {
	const match = fileName.match(FILE_NAME_PATTERN);
	if (!match) return null;
	return { type: TYPE_CASING[match[1].toLowerCase()], batchRunId: match[2] };
};

const validateFile = (file, enteredBatchRunId) => {
	const detected = detectFile(file.name);
	if (!detected) {
		return { file, type: null, valid: false, error: "Doesn't match the expected PeopleSoft file naming convention (SP_Type.BatchRunId.csv)" };
	}
	if (detected.batchRunId !== enteredBatchRunId) {
		return { file, type: detected.type, valid: false, error: `File's batch run ID "${detected.batchRunId}" doesn't match the entered batch run ID "${enteredBatchRunId}"` };
	}
	return { file, type: detected.type, valid: true, error: null };
};

export const AdminPeopleSoftUpload = (props) => {
	const [batchRunId, setBatchRunId] = useState("");
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadResults, setUploadResults] = useState(null);
	const [uploadedBatchRunId, setUploadedBatchRunId] = useState("");
	const [triggerMsg, setTriggerMsg] = useState("");
	const [triggerLevel, setTriggerLevel] = useState("");
	const [isGeneratingTrigger, setIsGeneratingTrigger] = useState(false);
	const [triggerGenerated, setTriggerGenerated] = useState(false);
	const [batchError, setBatchError] = useState("");
	const fileInputRef = useRef(null);

	useEffect(() => {
		props.onChildUpdate();
	}, []);

	const onFilesSelected = (files) => {
		if (!files || files.length === 0) return;
		const validated = Array.from(files).map((f) => validateFile(f, batchRunId));
		setSelectedFiles(validated);
		setUploadResults(null);
		setBatchError("");
	};

	const onFileDrop = (files, event) => {
		event.preventDefault();
		onFilesSelected(files);
	};

	const onFileInputChange = (e) => {
		onFilesSelected(e.target.files);
	};

	const removeSelectedFile = (idx) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const hasValidFiles = selectedFiles.some((f) => f.valid);
	const isBatchRunIdValid = BATCH_RUN_ID_PATTERN.test(batchRunId);

	const handleUpload = async () => {
		setBatchError("");
		const validFiles = selectedFiles.filter((f) => f.valid);
		const totalSize = validFiles.reduce((sum, f) => sum + f.file.size, 0);
		if (totalSize > MAX_BATCH_SIZE_BYTES) {
			setBatchError("The total size of the files in this batch exceeds the 250MB limit.");
			return;
		}

		setIsUploading(true);
		const formData = new FormData();
		formData.append("batchRunId", batchRunId);
		validFiles.forEach((f) => formData.append(FORM_KEY[f.type], f.file));

		const result = await ServiceCalls.uploadPeopleSoftFiles(formData);
		setUploadResults(result ? result.files : []);
		if (result && result.errorMessage) {
			setBatchError(result.errorMessage);
		}
		if (result && result.isSuccessful) {
			setUploadedBatchRunId(batchRunId);
		}
		setSelectedFiles([]);
		setIsUploading(false);
	};

	const handleGenerateTrigger = async () => {
		setIsGeneratingTrigger(true);
		const result = await ServiceCalls.generatePeopleSoftTrigger(batchRunId);
		const success = result && result.isSuccessful;
		setTriggerMsg(success ? `Trigger file generated for batch run ID ${batchRunId}. The batch process has been initiated.` : (result && result.errorMessage) || "Failed to generate the trigger file.");
		setTriggerLevel(success ? "success" : "danger");
		setTriggerGenerated(success);
		setIsGeneratingTrigger(false);
	};

	const showGenerateTrigger = uploadedBatchRunId && uploadedBatchRunId === batchRunId;

	return (
		<div className="peopleSoftUploadArea">
			<div className="eftDetailText" style={{ marginBottom: "10px" }}>
				Enter the batch run ID provided for this PeopleSoft run before selecting files. Every file uploaded in this batch must share this same batch run ID, which is also what gets written to the trigger file to start the batch process.
			</div>
			<Form.Group className="mb-3" controlId="batchRunId">
				<Form.Label>Batch Run Id</Form.Label>
				<Form.Control
					type="text"
					style={{ width: "260px" }}
					value={batchRunId}
					onChange={(e) => {
						setBatchRunId(e.target.value);
						setSelectedFiles([]);
						setUploadResults(null);
						setTriggerMsg("");
						setTriggerGenerated(false);
					}}
					placeholder="Enter the batch run ID"
				/>
			</Form.Group>

			{!batchRunId ? (
				<div className="error-message">Enter a batch run ID before selecting files to upload.</div>
			) : !isBatchRunIdValid ? (
				<div className="error-message">{INVALID_BATCH_RUN_ID_MESSAGE}</div>
			) : (
				<Container fluid className="peopleSoftFileUploadArea">
					<div className="eftStepHdrRow">
						<span className="eftStepHdrText">Upload Source Files</span>
					</div>
					<div className="eftFileUpload" style={{ marginTop: "10px", marginBottom: "10px" }}>
						<input onChange={onFileInputChange} ref={fileInputRef} type="file" multiple style={{ display: "none" }} />
						<FileDrop onDrop={onFileDrop} onTargetClick={() => fileInputRef.current.click()}>
							<div className="vCenter">Click to browse for files or drag and drop them here. Select any mix of Supplier, Invoice, InvoicePaymentXREF, and Payment files.</div>
						</FileDrop>
					</div>

					{selectedFiles.length > 0 && (
						<div className="pop-ups-container" style={{ padding: "10px", marginBottom: "10px" }}>
							{selectedFiles.map((f, idx) => (
								<div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
									<span>{f.file.name}</span>
									<span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
										{f.valid ? <span style={{ color: "green" }}>Valid</span> : <span className="error-message" style={{ margin: 0 }}>{f.error}</span>}
										<button type="button" title="Remove file" className="uploadFileImg" onClick={() => removeSelectedFile(idx)}>
											<FontAwesomeIcon icon={faTrashCan} />
										</button>
									</span>
								</div>
							))}
						</div>
					)}

					{batchError && <div className="error-message" style={{ marginBottom: "10px" }}>{batchError}</div>}

					{isUploading ? (
						<Loading />
					) : (
						<Button className="popup-buttons" style={{ float: "none" }} disabled={!hasValidFiles} onClick={handleUpload}>
							Upload files
						</Button>
					)}
				</Container>
			)}

			{uploadResults && (
				<div style={{ marginTop: "16px" }}>
					{uploadResults.map((r, idx) => (
						<Alert key={idx} variant={r.isSuccessful ? "success" : "danger"}>
							{r.fileType}: {r.isSuccessful ? `uploaded successfully${r.wasOverwritten ? " (replaced an existing file for this batch run)" : ""}` : r.errorMessage}
						</Alert>
					))}
				</div>
			)}

			{showGenerateTrigger && (
				<div style={{ marginTop: "16px" }}>
					{triggerMsg && <Alert variant={triggerLevel}>{triggerMsg}</Alert>}
					{isGeneratingTrigger ? (
						<Loading />
					) : triggerGenerated ? (
						<Button className="popup-buttons" style={{ float: "none" }} onClick={props.onViewFiles}>
							Show Files
						</Button>
					) : (
						<Button className="popup-buttons" style={{ float: "none" }} onClick={handleGenerateTrigger}>
							Generate trigger file
						</Button>
					)}
				</div>
			)}
		</div>
	);
};
